import {
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useDueFollowups, useSalesDashboard } from '@/api/hooks'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  MetricCard,
  PageHeader,
  StatusChip,
} from '@/components/common'
import { useChartTokens } from '@/theme'

export function SalesDashboardPage() {
  const chart = useChartTokens()
  const { data, isLoading, error } = useSalesDashboard()
  const { data: followups } = useDueFollowups()

  return (
    <>
      <PageHeader
        title="Sales dashboard"
        subtitle="Pipeline, follow-up discipline and quotation ageing"
      />
      <ErrorBanner error={error} />
      {isLoading && <InlineSpinner />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Follow-ups due today"
                value={formatNumber(data.followupsDueToday)}
                tone={data.followupsDueToday > 0 ? 'warning' : 'default'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Overdue follow-ups"
                value={formatNumber(data.followupsOverdue)}
                tone={data.followupsOverdue > 0 ? 'error' : 'success'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Weighted pipeline"
                value={formatMoney(data.weightedPipelineValue)}
                caption="Open enquiries × probability"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Awaiting approval"
                value={formatNumber(data.quotationsAwaitingApproval)}
                caption="Quotations held on the discount matrix"
                tone={data.quotationsAwaitingApproval > 0 ? 'warning' : 'default'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Tentative holds expiring"
                value={formatNumber(data.tentativeBookingsExpiring)}
                caption="Within the next three days"
                tone={data.tentativeBookingsExpiring > 0 ? 'warning' : 'default'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Quotation value sent" value={formatMoney(data.quotationValueSent)} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Quotation ageing
                  </Typography>
                  {data.quotationAgeing.every((bucket) => bucket.count === 0) ? (
                    <EmptyState message="No quotations are currently awaiting a decision." />
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={data.quotationAgeing}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis dataKey="name" fontSize={12} stroke={chart.axis} />
                        <YAxis allowDecimals={false} fontSize={12} stroke={chart.axis} />
                        <Tooltip
                          formatter={(value: number, name) =>
                            name === 'amount' ? formatMoney(value) : formatNumber(value)
                          }
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                          cursor={{ fill: chart.grid }}
                        />
                        <Bar dataKey="count" name="Quotations" fill={chart.series[1]} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Enquiry funnel
                  </Typography>
                  {data.conversionBySource.length === 0 ? (
                    <EmptyState message="No enquiries in the selected window." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Stage</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Weighted value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.conversionBySource.map((row) => (
                          <TableRow key={row.name} hover>
                            <TableCell>
                              <StatusChip status={row.name} />
                            </TableCell>
                            <TableCell align="right">{formatNumber(row.count)}</TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Pipeline by salesperson
                  </Typography>
                  {data.enquiriesBySalesperson.length === 0 ? (
                    <EmptyState message="Nothing assigned yet." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Owner</TableCell>
                          <TableCell align="right">Enquiries</TableCell>
                          <TableCell align="right">Weighted value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.enquiriesBySalesperson.slice(0, 10).map((row) => (
                          <TableRow key={row.name} hover>
                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.name === 'Unassigned' ? 'Unassigned' : row.name.slice(0, 8)}
                            </TableCell>
                            <TableCell align="right">{formatNumber(row.count)}</TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Follow-up calendar
                  </Typography>
                  {!followups || followups.length === 0 ? (
                    <EmptyState message="No follow-ups scheduled in the next seven days." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Due</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {followups.slice(0, 12).map((followup) => (
                          <TableRow key={followup.id} hover>
                            <TableCell>{formatDateTime(followup.scheduledAt)}</TableCell>
                            <TableCell>{humanise(followup.followupType)}</TableCell>
                            <TableCell
                              sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {followup.notes ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}
